import type { Dictionary, WorkerPostMessage, WorkerShutdown } from "@/core/types";
import type { AVMediaType, FileInfo, MediaType } from "./structReader";
import type { OutsideSource, SeekerWorkerInit } from "../seeker/types";

//#region Main -> FFmpeg Worker
export interface WorkerInitFFmpeg extends WorkerPostMessage {
    readonly kind: "initFfmpeg";
    readonly fileSource: string | File | OutsideSource;
    readonly bufferSize: number;
}

export interface WorkerInitFFmpegOnlyModule extends WorkerPostMessage {
    readonly kind: "initFfmpegModuleOnly";
    readonly url?: string;
}

export interface WorkerChangeStream extends WorkerPostMessage {
    readonly kind: "changeStream";
    readonly index: number;
    readonly enabled: boolean;
}

export interface WorkerOk extends WorkerPostMessage {
    readonly kind: "ok";
}

export interface WorkerRequestAnswered extends WorkerPostMessage {
    readonly kind: "requestAnswered";
    readonly status?: boolean;
}

export interface WorkerEndOfFile extends WorkerPostMessage {
    readonly kind: "endOfFile";
}

export interface WorkerThumbnailDone extends WorkerPostMessage {
    readonly kind: "thumbnailDone";
    readonly return: number;
}

export interface WorkerThumbnailInProgress extends WorkerPostMessage {
    readonly kind: "thumbnailInProgress";
}

export interface WorkerSeekResult extends WorkerPostMessage {
    readonly kind: "doneSeeking";
    readonly return: number;
}

export interface WorkerMediaInfo extends WorkerPostMessage {
    readonly kind: "mediaInfo";
    readonly data: Dictionary<string>;
}

export interface WorkerEmbedFont extends WorkerPostMessage {
    readonly kind: "fontFile";
    readonly data: Uint8Array;
    readonly fontFamily: string | null;
    readonly fileName: string;
}

export interface WorkerPostPort extends WorkerPostMessage {
    readonly kind: "portPost";
    readonly streamIndex: number;
    readonly port: MessagePort;
}

export interface WorkerBitmapSubtitle extends WorkerPostMessage {
    readonly kind: "subtitleBitmap";
    readonly streamIndex: number,
    readonly image: Blob;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    /** Time in microseconds */
    readonly timestamp: number;
    /** Time in milliseconds */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    readonly start_time: number;
    /** Time in milliseconds */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    readonly end_time: number;
}


export interface WorkerAssSubtitle extends WorkerPostMessage {
    readonly kind: "subtitleAss";
    readonly streamIndex: number,
    readonly dialog: string;
}

export interface WorkerRequestThumbnail extends WorkerPostMessage {
    readonly kind: "thumbnailRequest";
    readonly url: string;
}

export interface WorkerRequestDemuxers extends WorkerPostMessage {
    readonly kind: "demuxerRequest";
}

export type AllTargetWorkerMessages =
    WorkerInitFFmpeg | WorkerChangeStream | // Video Player
    WorkerRequestDemuxers | // Demuxers and Exif
    WorkerShutdown;


//#endregion


//#region FFmpeg Worker -> Main Thread

export interface WorkerFFmpegInitStatus extends WorkerPostMessage {
    readonly kind: "initFFmpegStatus";
    readonly status: number;
    readonly info: FileInfo | null;
    readonly streamPorts: Map<number, MessagePort> | null;
}

export interface WorkerSetTime extends WorkerPostMessage {
    readonly kind: "setTime";
    readonly time: bigint;
}

export interface WorkerEndOfFile extends WorkerPostMessage {
    readonly kind: "endOfFile";
}

export interface WorkerRequestData extends WorkerPostMessage {
    readonly kind: "requestData";
}

export interface WorkerDataAnswer extends WorkerPostMessage {
    readonly kind: "dataAnswer";
    readonly status: RequestDataStatus;
    readonly packetType: MediaType;
}

export interface WorkerSeekTo extends WorkerPostMessage {
    readonly kind: "seekTo";
    readonly time: number;
}

export interface WorkerSeekStatus extends WorkerPostMessage {
    readonly kind: "seekStatus";
    readonly status: number;
}

export type AllVideoWorkerEvents = WorkerFFmpegInitStatus | WorkerSetTime | WorkerOk | WorkerEndOfFile | WorkerRequestData | WorkerDataAnswer | WorkerSeekTo | WorkerSeekStatus | SeekerWorkerInit;

//#endregion

export type AllRespondWorkerEventsKind = AllVideoWorkerEvents["kind"];
export type RespondEventByKind<E extends AllRespondWorkerEventsKind> = Extract<AllVideoWorkerEvents, { kind: E }>;
export type DictionaryWorkerEvent = {
  [K in AllRespondWorkerEventsKind]: ((data: RespondEventByKind<K>) => void)[];
};

export enum StreamSupport {
    HW_SUPPORT = 1,
    SW_SUPPORT = 0,
    UNUSED = -1,
    NO_SUPPORT = -2,
}

export enum RequestDataStatus {
    EOF = -2,
    ERR = -1,
    IMMEDIATE_RESPOSNE = 0,
    DECODED_BY_OTHER_THREAD = 1
}
  
export type ValidDecoderTypes = keyof DecoderConfig & keyof DecoderSupport;
export type DecoderConfig = {
    [AVMediaType.AVMEDIA_TYPE_VIDEO]: VideoDecoderConfig,
    [AVMediaType.AVMEDIA_TYPE_AUDIO]: AudioDecoderConfig
}
export type DecoderSupport = {
    [AVMediaType.AVMEDIA_TYPE_VIDEO]: VideoDecoderSupport,
    [AVMediaType.AVMEDIA_TYPE_AUDIO]: AudioDecoderSupport
}
